/**
 * Pure sidebar sections for enabled extensions (HUB-103): ONE section per
 * extension, headed by its display name, holding each custom `nav` entry
 * verbatim and then a "Settings" row when the extension declares a manifest.
 * An extension with neither yields no section — and a row is never shown
 * without the heading that says which extension it belongs to. `name:
 * 'extension'` is the shared ICONS key. Hub-client-free so
 * `sidebar-sections.test` can import it.
 */
import type { NavSection } from '@/components/Sidebar';
import type { ExtensionNavEntry } from './registry';
import { extensionAdminRoute } from './registry';

export interface ExtensionNavSource {
  name: string;
  hasSettings: boolean;
  nav?: ExtensionNavEntry[];
}

/** `'crm_sync'` → `'Crm sync'`. */
export function extensionDisplayName(name: string): string {
  const spaced = name.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Sidebar heading for an extension: "Example extension". */
export function extensionSectionLabel(name: string): string {
  return `${extensionDisplayName(name)} extension`;
}

export function buildExtensionNavSections(sources: ExtensionNavSource[]): NavSection[] {
  return sources.flatMap((ext) => {
    const custom = (ext.nav ?? []).map((entry) => ({
      key: `ext-${ext.name}-${entry.path || 'root'}`,
      name: 'extension',
      route: extensionAdminRoute(ext.name, entry.path),
      label: entry.label,
    }));
    const settings = ext.hasSettings
      ? [{
          key: `ext-${ext.name}-settings`,
          name: 'extension',
          route: extensionAdminRoute(ext.name, 'settings'),
          label: 'Settings',
        }]
      : [];
    const items = [...custom, ...settings];
    return items.length > 0 ? [{ label: extensionSectionLabel(ext.name), items }] : [];
  });
}
